import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import { normalizeLaborMatchKey } from "@/lib/manufacturing/laborCompanyPayMatch";

export type LaborRateRecipePick = {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  kind?: MfgRecipeKind;
};

export const LABOR_RATE_RECIPE_KIND_DEFAULT: MfgRecipeKind = "product";

export const LABOR_RATE_RECIPE_KIND_CHOICES: ReadonlyArray<{ id: MfgRecipeKind; label: string }> = [
  { id: "product", label: "Product (top-level SKU)" },
  { id: "option", label: "Option" },
  { id: "package", label: "Package" },
  { id: "assembly", label: "Assembly" },
  { id: "variant", label: "Variant" },
];

const ALL_KINDS = LABOR_RATE_RECIPE_KIND_CHOICES.map((c) => c.id);

export function defaultLaborRateRecipeKinds(): Set<MfgRecipeKind> {
  return new Set<MfgRecipeKind>([LABOR_RATE_RECIPE_KIND_DEFAULT]);
}

export function parseLaborRateRecipeKinds(raw: unknown): Set<MfgRecipeKind> {
  const allowed = new Set<string>(ALL_KINDS);
  const values = Array.isArray(raw) ? raw.filter((k): k is MfgRecipeKind => allowed.has(String(k))) : [];
  if (values.length === 0) return defaultLaborRateRecipeKinds();
  return new Set(values);
}

export function allLaborRateRecipeKindsSelected(kinds: ReadonlySet<MfgRecipeKind>): boolean {
  return ALL_KINDS.every((k) => kinds.has(k));
}

export function toggleLaborRateRecipeKindSelectAll(selectAll: boolean): Set<MfgRecipeKind> {
  return selectAll ? new Set(ALL_KINDS) : defaultLaborRateRecipeKinds();
}

export function toggleLaborRateRecipeKind(
  current: ReadonlySet<MfgRecipeKind>,
  kind: MfgRecipeKind,
  checked: boolean
): Set<MfgRecipeKind> {
  const next = new Set(current);
  if (checked) next.add(kind);
  else next.delete(kind);
  if (next.size === 0) return defaultLaborRateRecipeKinds();
  return next;
}

export function filterRecipesForLaborRateMatch<T extends LaborRateRecipePick>(
  recipes: readonly T[],
  allowedKinds: ReadonlySet<MfgRecipeKind>,
  selectedId = ""
): T[] {
  return recipes.filter((r) => {
    if (selectedId && r.id === selectedId) return true;
    if (!r.is_active) return false;
    if (!r.kind) return allowedKinds.has(LABOR_RATE_RECIPE_KIND_DEFAULT);
    return allowedKinds.has(r.kind);
  });
}

/** Exact code or name match so unmatched-inbox seeds can auto-select a recipe. */
export function findRecipeForLaborMatchKey<T extends LaborRateRecipePick>(
  recipes: readonly T[],
  query: string
): T | null {
  const key = normalizeLaborMatchKey(query);
  if (!key) return null;
  const hits = recipes.filter((r) => {
    const code = normalizeLaborMatchKey(r.code);
    const name = normalizeLaborMatchKey(r.name);
    return code === key || name === key;
  });
  return hits.length === 1 ? hits[0] : null;
}

/** Infer a Models kind when parking an unmatched pay item as a new recipe. */
export function guessLaborRateRecipeKind(notes: string, name: string): MfgRecipeKind {
  const n = notes.trim().toLowerCase();
  if (n === "product" || n === "building") return "product";
  if (n === "package" || n === "variant" || n === "assembly" || n === "option") return n;
  if (/\d+\s*x\s*\d+/i.test(name) && /(utility|lofted|studio|bullet|wall)/i.test(name)) {
    return "product";
  }
  return "option";
}

export function applyRecipeToCompanyRateDraft<T extends {
  recipe_id: string;
  item_code: string;
  option_code: string;
  label: string;
}>(draft: T, recipe: LaborRateRecipePick): T {
  return {
    ...draft,
    recipe_id: recipe.id,
    item_code: recipe.code,
    option_code: recipe.code,
    label: draft.label.trim() ? draft.label : recipe.name,
  };
}
